import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';

// SiteUp: two WhatsApp clocks (never mix):
// 1) CSW — `messaging_window_expires_at` — free-form capability (typically 24h).
// 2) FEP — `messaging_window_fep_expires_at` — CTWA pricing only (72h); templates
//    may be billing-free while open; does NOT extend free-form.
//
// Urgency thresholds (CSW remaining): ok > 4h, warning 1–4h, urgent < 1h, expired.

const globalNow = ref(Date.now());
let globalTimer = null;
let refCount = 0;

const startClock = () => {
  refCount += 1;
  if (!globalTimer) {
    globalTimer = setInterval(() => {
      globalNow.value = Date.now();
    }, 30000);
  }
};

const stopClock = () => {
  refCount -= 1;
  if (refCount <= 0 && globalTimer) {
    clearInterval(globalTimer);
    globalTimer = null;
    refCount = 0;
  }
};

const toEpochMs = raw => {
  if (!raw) return null;
  const seconds = Number(raw);
  return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : null;
};

const formatDuration = remainingMs => {
  const totalMinutes = Math.max(0, Math.floor((remainingMs || 0) / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return { hours, minutes };
};

export function useMessagingWindow(getChat) {
  const { t } = useI18n();

  onMounted(startClock);
  onBeforeUnmount(stopClock);

  const resolveChat = () =>
    typeof getChat === 'function' ? getChat() : getChat;

  const expiresAtMs = computed(() => {
    const chat = resolveChat();
    return toEpochMs(
      chat?.messaging_window_expires_at ?? chat?.messagingWindowExpiresAt
    );
  });

  const fepExpiresAtMs = computed(() => {
    const chat = resolveChat();
    return toEpochMs(
      chat?.messaging_window_fep_expires_at ?? chat?.messagingWindowFepExpiresAt
    );
  });

  // 'standard' | 'ad' | null — ad = CTWA lead; FEP is separate.
  const kind = computed(() => {
    const chat = resolveChat();
    return chat?.messaging_window_kind ?? chat?.messagingWindowKind ?? null;
  });
  const isAd = computed(() => kind.value === 'ad');

  const hasWindow = computed(() => expiresAtMs.value !== null);

  const remainingMs = computed(() =>
    expiresAtMs.value === null ? null : expiresAtMs.value - globalNow.value
  );

  const isExpired = computed(
    () => remainingMs.value !== null && remainingMs.value <= 0
  );

  const level = computed(() => {
    if (!hasWindow.value) return null;
    if (isExpired.value) return 'expired';
    const hours = remainingMs.value / 3600000;
    if (hours < 1) return 'urgent';
    if (hours <= 4) return 'warning';
    return 'ok';
  });

  const label = computed(() => {
    if (!hasWindow.value) return '';
    if (isExpired.value) {
      return t('CONVERSATION.MESSAGING_WINDOW.CLOSED');
    }
    const { hours, minutes } = formatDuration(remainingMs.value);
    return hours >= 1
      ? t('CONVERSATION.MESSAGING_WINDOW.REMAINING_HM', { hours, minutes })
      : t('CONVERSATION.MESSAGING_WINDOW.REMAINING_M', { minutes });
  });

  const shortLabel = computed(() => {
    if (!hasWindow.value) return '';
    if (isExpired.value) {
      return t('CONVERSATION.MESSAGING_WINDOW.CLOSED_SHORT');
    }
    const { hours, minutes } = formatDuration(remainingMs.value);
    return hours >= 1
      ? t('CONVERSATION.MESSAGING_WINDOW.SHORT_H', { hours })
      : t('CONVERSATION.MESSAGING_WINDOW.SHORT_M', { minutes });
  });

  const badgeClass = computed(() => {
    switch (level.value) {
      case 'expired':
        return 'bg-n-ruby-9/20 text-n-ruby-11';
      case 'urgent':
        return 'bg-n-ruby-9/15 text-n-ruby-11';
      case 'warning':
        return 'bg-n-amber-9/15 text-n-amber-11';
      case 'ok':
        return 'bg-n-teal-9/15 text-n-teal-11';
      default:
        return '';
    }
  });

  const icon = computed(() =>
    level.value === 'expired' ? 'i-ri-lock-line' : 'i-ri-time-line'
  );

  const isUrgent = computed(() => level.value === 'urgent');

  const fepRemainingMs = computed(() =>
    fepExpiresAtMs.value === null
      ? null
      : fepExpiresAtMs.value - globalNow.value
  );

  const fepActive = computed(
    () => fepRemainingMs.value !== null && fepRemainingMs.value > 0
  );

  const fepLabel = computed(() => {
    if (!fepActive.value) return '';
    const { hours, minutes } = formatDuration(fepRemainingMs.value);
    const until =
      hours >= 1
        ? t('CONVERSATION.MESSAGING_WINDOW.REMAINING_HM', { hours, minutes })
        : t('CONVERSATION.MESSAGING_WINDOW.REMAINING_M', { minutes });
    return t('CONVERSATION.MESSAGING_WINDOW.FEP_BADGE', { until });
  });

  const fepShortLabel = computed(() => {
    if (!fepActive.value) return '';
    const { hours, minutes } = formatDuration(fepRemainingMs.value);
    const until =
      hours >= 1
        ? t('CONVERSATION.MESSAGING_WINDOW.SHORT_H', { hours })
        : t('CONVERSATION.MESSAGING_WINDOW.SHORT_M', { minutes });
    return t('CONVERSATION.MESSAGING_WINDOW.FEP_SHORT', { until });
  });

  const tooltip = computed(() => {
    if (!hasWindow.value) return '';
    if (isExpired.value) {
      if (fepActive.value) {
        return t('CONVERSATION.MESSAGING_WINDOW.TOOLTIP_EXPIRED_FEP', {
          until: fepLabel.value,
        });
      }
      return t('CONVERSATION.MESSAGING_WINDOW.TOOLTIP_EXPIRED');
    }
    if (isAd.value) {
      return t('CONVERSATION.MESSAGING_WINDOW.TOOLTIP_AD', {
        remaining: label.value,
      });
    }
    return t('CONVERSATION.MESSAGING_WINDOW.TOOLTIP_OPEN', {
      remaining: label.value,
    });
  });

  return {
    hasWindow,
    remainingMs,
    isExpired,
    isUrgent,
    level,
    label,
    shortLabel,
    badgeClass,
    icon,
    tooltip,
    kind,
    isAd,
    fepActive,
    fepLabel,
    fepShortLabel,
    fepRemainingMs,
  };
}
